/**
 * Render the site banner at the top of the site.
 *
 * Which banners are displayed is configured by the settings in
 * `site-banners-config.js`. The banners themselves can be found inside the
 * banners directory.
 */
import {useFeatureIsOn, useFeatureValue} from "@growthbook/growthbook-react";
import * as React from "react";
import stringHash from "string-hash";

import {
    RenderState,
    useIsMounted,
    useRenderState,
} from "@khanacademy/wonder-blocks-core";
import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {useGql} from "@khan/data-gql";
import Log from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import useProfile from "#data-access/use-profile.ts";
import LocalStore from "#shared-package/local-store.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import {getBanners} from "./site-banners-config.ts";
import type {BannerConfig, BannerFlags} from "./types.ts";

/**
 * List of paths that shouldn't display site banners
 */
const DISALLOWED_PATHS = [
    "/completesignup",
    "/about/docs/khan-academy-terms-of-service",
    "/about/privacy-policy",
    "/translations",
    "/devadmin/content",
];

/**
 * Helper to generate user-specific dismiss keys.
 *
 * By making dismiss keys user specific this avoids a situation where one user
 * could dismiss a site banner would prevent another user using the same machine
 * from seeing that banner.
 *
 * NOTE: This doesn't do anything to address the issue of logged-out users
 * dismissing banners from affect what banners other logged out users see.
 */
const getDismissKey: (
    arg1: string,
    arg2?: ProfileModelType | null | undefined,
) => string = (bannerName, profile) => {
    return profile
        ? `${bannerName}_${stringHash(profile.get("kaid"))}`
        : bannerName;
};

type SiteOutageBanner = BannerFlags["siteOutageBanner"];

type Props = {
    // Placeholder to render if a user should see banners but there is no
    // banner to show. Used to detect this case in tests.
    noBannerPlaceholder?: React.ReactElement;
    // Placeholder to render if a user should NOT see banners. Used to detect
    // this case in tests.
    noDisplayPlaceholder?: React.ReactElement;
};

// All "no display" cases must be negative numbers. A positive index indicates
// the index of the banner to display.
const BANNER_SELECTION_PENDING = -1;
const BANNER_SELECTION_NO_BANNER = -2;

const SiteBanners = (props: Props) => {
    const [dismissed, setDismissed] = React.useState<Array<string>>([]);
    const isMounted = useIsMounted();
    const curBannerName = React.useRef<string | null>(null);
    const location = useLocation();
    const gqlFetch = useGql();
    const renderState = useRenderState();
    const profileResult = useProfile();
    const profile =
        profileResult.status === "success"
            ? (profileResult.profile ?? null)
            : null;
    const siteOutageBanner = useFeatureValue<SiteOutageBanner>(
        "site-outage-banner",
        {},
    );
    const showCheatingBannerForTeachers = useFeatureIsOn<AppFeatures>(
        "cheating-banner-for-teachers",
    );
    const flags: BannerFlags = React.useMemo(
        () => ({
            siteOutageBanner,
            showCheatingBannerForTeachers,
        }),
        [siteOutageBanner, showCheatingBannerForTeachers],
    );

    const [bannerIndex, setBannerIndex] = React.useState<number>(
        BANNER_SELECTION_PENDING,
    );

    React.useEffect(() => {
        selectBanner(getBanners()).then((index) => {
            if (isMounted()) {
                setBannerIndex(index);
            }
        });
        // selectBanner isn't in the dependency array because it doesn't
        // functionally change with each render. See the TODO above the function;
        // the function should really be pulled out of this component.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gqlFetch, location, profile, flags, isMounted]);

    /**
     * Handle dismissing a banner.
     *
     * Stores the result in localStorage and updates the local state to trigger
     * a re-render of the SiteBanners component.
     */
    const handleDismiss = (): void => {
        const name = curBannerName.current;

        // If there is no banner open, we bail. This should never happen.
        if (!name) {
            return;
        }

        const dismissKey = getDismissKey(name, profile);

        // Track if the banner was dismissed.
        LocalStore.set(dismissKey, true);

        if (isMounted()) {
            // We keep track of which banners have been dismissed in the local
            // state, just in case localStorage isn't working (and this will also
            // trigger a re-render).
            setDismissed([...dismissed, dismissKey]);
        }

        // Reset the banner name
        curBannerName.current = null;
    };

    /**
     * Determine if a specific banner has been dismissed.
     *
     * We check both localStorage and the list of dismissed banners, in case
     * the user's localStorage isn't working.
     *
     * @param {string} name The name of the banner to check.
     */
    const isDismissed = (name: string): boolean => {
        const dismissKey = getDismissKey(name, profile);

        return (
            LocalStore.get(dismissKey) === true ||
            dismissed.includes(dismissKey)
        );
    };

    /**
     * Determine if we can show the site banners.
     */
    const shouldDisplay = (): boolean => {
        // If the feature flag is off then we aren't going to show any banners
        // We also wait until we have the profile loaded before attempting to
        // render (even if the user is logged out). We never render banners
        // during server-side render and finally we make sure we don't render
        // on certain routes. ?learn=1 comes from parents and teachers wanting
        // to access the learning homepage.
        return !!(
            profileResult.status === "success" &&
            // We check if this is a non-SSR/hydration render before isE2E as
            // we'll never display the banner during initial render and we can
            // avoid accessing the isE2E property unnecessarily.
            renderState === RenderState.Standard &&
            !RequestInfo.isE2E &&
            !RequestInfo.isBot &&
            !DISALLOWED_PATHS.includes(location.pathname) &&
            !location.queryParams.embedded &&
            !location.queryParams.learn
        );
    };

    /**
     * Determine which banner we should be rendering.
     *
     * We go through each banner and check to see if it's already been dismissed
     * or if it should even be rendered for this page/user. Then we check any
     * feature flag that may be needed to verify if we should be showing the
     * banner.
     *
     * If all of those succeed we dynamically load the first banner that
     * matches all of those qualifications.
     */
    // TODO(marksandstrom) Pull this function out of the component so the
    // dependencies are obvious. useEffect above calls this function, and the
    // function depends on profile, location, flags and gqlFetch. If another
    // dependency is added, it would be easy to forget to update the
    // useEffect dependencies.
    const selectBanner = async (
        banners: Array<BannerConfig>,
    ): Promise<number> => {
        for (let i = 0; i < banners.length; i++) {
            const banner = banners[i];

            // Ignore banners that've been dismissed already
            if (isDismissed(banner.name)) {
                continue;
            }

            // Ignore banners that we don't need to render. We wait the result
            // in case the banner needs to do some other async behavior to
            // determine if it should render.
            try {
                const shouldRender = await banner.shouldRender(
                    {
                        profile,
                        location,
                        flags,
                    },
                    gqlFetch,
                );

                if (!shouldRender) {
                    continue;
                }
            } catch (error: any) {
                // An error came back when determining if we should render the
                // banner, so we just log it and move on
                Log.log(`Error failing to render banner ${banner.name}`, {
                    error,
                });
                continue;
            }

            curBannerName.current = banner.name;
            return i;
        }

        return BANNER_SELECTION_NO_BANNER;
    };

    if (bannerIndex === BANNER_SELECTION_NO_BANNER) {
        return props.noBannerPlaceholder ?? null;
    }

    // If banner index is less than zero and not BANNER_SELECTION_NO_BANNER,
    // we are still trying to determine which banner to display (if any).
    if (bannerIndex < 0) {
        return null;
    }

    if (!shouldDisplay()) {
        return props.noDisplayPlaceholder ?? null;
    }

    const BannerComponent = getBanners()[bannerIndex].component;

    return (
        <React.Suspense fallback={null}>
            <BannerComponent
                profile={profile}
                location={location}
                flags={flags}
                onDismiss={handleDismiss}
            />
        </React.Suspense>
    );
};

export default SiteBanners;
